// CloudShadows.tsx — Dramatic cloud shadows cast onto the surface. For each surface
// point we trace a ray TOWARD the sun to where it pierces the cloud layer and sample
// the (shared) cloud coverage there: if a cloud sits up-sun, the ground is in shadow.
// The geometry gives the terminator drama for free — at a low sun the up-sun hit is
// far toward the horizon, so the shadow comes from a distant cloud and STRETCHES (the
// long anvil-cloud shadows you see from orbit at sunset); at noon it's the cloud
// directly overhead (a short shadow).
//
// Rendered as a thin shell at the surface with MULTIPLY blending, so it simply darkens
// the already-drawn ground — no edits to the (heavily-shared) surface shader. It does
// NOT spin with the surface: it lives in the cloud deck's frame and samples the same
// cloudCover field as Clouds.tsx, so the shadows track the visible clouds exactly and
// drift over the rotating terrain beneath, the way real cloud shadows do.
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { MultiplyBlending, Vector3 } from 'three';
import type { DataTexture, Mesh } from 'three';
import { SIMPLEX3D } from './planet/glslNoise';
import { CLOUD_FIELD_PARS, makeCloudFieldUniforms } from './planet/cloudField';
import { gameClock } from '../state/gameClock';

// The shadow shell sits a hair above the surface (clear of all but the tallest peaks)
// so it darkens the ground rather than z-fighting it.
const SHELL_SCALE = 1.02;

const vert = /* glsl */ `
  varying vec3 vObjDir;
  void main() {
    vObjDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const frag = /* glsl */ `
  precision highp float;
  #define PI 3.14159265359
  uniform vec3 uSunLocalDir;   // sun direction in the deck's local (object) frame
  uniform float uCloudScale;   // cloud-layer radius ÷ surface radius (sets shadow length)
  uniform float uShadowStr;    // 0..1 darkening at full cloud cover
  uniform sampler2D uWeather;  // SAME shared seasonal cloud-cover texture the deck samples
  uniform float uHasWeather;   // 1 once the baked weather video has loaded
  varying vec3 vObjDir;
  ${SIMPLEX3D}
  ${CLOUD_FIELD_PARS}

  // Equirect UV for a deck-local direction — identical to Clouds.tsx's weatherUV so the gate
  // reads the same texel the deck does.
  vec2 weatherUV(vec3 d){
    float lon = atan(d.z, d.x);
    float lat = asin(clamp(d.y, -1.0, 1.0));
    return vec2((lon + PI) / (2.0 * PI), 0.5 - lat / PI);
  }

  void main(){
    vec3 P = normalize(vObjDir);     // surface point direction (== its up vector)
    vec3 S = normalize(uSunLocalDir);
    float c = dot(P, S);             // sun elevation cosine; <0 = night
    // Fade the shadows out as the sun drops toward the horizon: at a grazing sun the up-sun hit
    // is far away and the shadow smears into a long, ugly, unnatural streak (very visible over a
    // tidally-locked world's fixed terminator ring). Only render them where the sun is reasonably
    // high, so a cloud casts a compact shadow roughly beneath itself.
    float dayVis = smoothstep(0.04, 0.34, c);
    if (dayVis < 0.002) discard;
    // March from the surface point toward the sun to the cloud sphere (radius
    // uCloudScale on the unit surface): solve |P + S t| = uCloudScale.
    float t = -c + sqrt(max(c * c + uCloudScale * uCloudScale - 1.0, 0.0));
    vec3 cloudDir = normalize(P + S * t);
    // Shadow the SAME moving baked coverage the deck renders (the wind-advected loop), so the
    // shadows track the flowing clouds exactly. De-grid the coarse cells with a snoise UV nudge so
    // they don't read as blocky patches. Before the loop loads, fall back to the procedural field.
    float cover;
    if (uHasWeather > 0.5) {
      vec2 uv = weatherUV(cloudDir) + vec2(snoise(cloudDir * 6.0), snoise(cloudDir * 6.0 + 41.3)) * 0.03;
      cover = smoothstep(0.30, 0.62, texture2D(uWeather, uv).r);
    } else {
      cover = cloudCover(cloudDir);
    }
    float mult = 1.0 - cover * uShadowStr * dayVis;
    gl_FragColor = vec4(vec3(mult), 1.0);
  }
`;

interface Props {
  planetRadius: number;
  /** World position of the star (== system.position). */
  sunWorldPos: Vector3;
  /** Same seed as the planet's Clouds, so the shadows match the visible deck. */
  seed: number;
  /** Same cloud cover (0..1) as Clouds. */
  coverage?: number;
  /** Cloud-layer radius ÷ surface radius — longer = clouds higher = longer shadows. */
  cloudScale?: number;
  /** The SAME shared seasonal cloud-cover texture the deck samples (built by Planet), so a
   *  shadow only falls where the deck actually renders cloud. */
  weatherTex: DataTexture;
  /** Whether the baked weather video is loaded (gates the weather term). */
  hasWeather: boolean;
  /** 1 for a tidally-locked world — kills the shadow field's zonal drift/belts so it stays
   *  glued to the (stationary) deck. */
  tidalLock: number;
}

export function CloudShadows({
  planetRadius,
  sunWorldPos,
  seed,
  coverage = 0.5,
  cloudScale = 1.1,
  weatherTex,
  hasWeather,
  tidalLock,
}: Props) {
  const meshRef = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);
  const sunLocal = useMemo(() => new Vector3(), []);

  const uniforms = useMemo(
    () => ({
      ...makeCloudFieldUniforms(seed, coverage, tidalLock),
      uSunLocalDir: { value: new Vector3(0, 0, 1) },
      uCloudScale: { value: cloudScale },
      // Softer than before (was 0.72): the old strong multiply made the shadows read as harsh,
      // unnatural dark blotches, especially off the coarse weather grid.
      uShadowStr: { value: 0.5 },
      uWeather: { value: weatherTex },
      uHasWeather: { value: hasWeather ? 1 : 0 },
    }),
    [seed, coverage, cloudScale, weatherTex, hasWeather, tidalLock],
  );

  useFrame(() => {
    if (!meshRef.current) {
      return;
    }
    // Drift in lockstep with Clouds.tsx (same game-clock anchoring), so the shadows
    // stay glued under the visible banks.
    if (startTime.current === null) {
      startTime.current = gameClock.time;
    }
    uniforms.uTime.value = gameClock.time - startTime.current;
    meshRef.current.worldToLocal(sunLocal.copy(sunWorldPos));
    uniforms.uSunLocalDir.value.copy(sunLocal).normalize();
  });

  return (
    // renderOrder 1.5: after the opaque surface (0) AND the ocean shell (1), so MULTIPLY
    // darkens both the land and the sea, but before the clouds (bumped to 2) so the
    // clouds themselves aren't darkened by their own shadow.
    <mesh ref={meshRef} scale={SHELL_SCALE} renderOrder={1.5}>
      <sphereGeometry args={[planetRadius, 48, 32]} />
      <shaderMaterial
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={MultiplyBlending}
      />
    </mesh>
  );
}
